import React from 'react';

import { Loader, Message, MessageContent, MessageResponse } from '@modernloop/ai-elements';
import { useTranslation } from '@modernloop/i18n';
import { ChatRole } from '@modernloop/shared/components';

import { AGENT_PENDING_SEGMENT_KEY, AGENT_TRANSCRIPT_SEGMENT } from '../agent/transcriptSegments';
import type { AgentTranscriptMessage, AgentTranscriptSegment } from '../agent/transcriptSegments';
import useAgentStreamingConfig from '../agent/useAgentStreamingConfig';

export type MessageRunSegment = Extract<AgentTranscriptSegment, { type: typeof AGENT_TRANSCRIPT_SEGMENT.MESSAGE_RUN }>;

type Props = {
  segment: MessageRunSegment;
  onLinkClick: (url: string, event: React.MouseEvent<HTMLAnchorElement>) => void;
};

type AgentMessageProps = {
  message: AgentTranscriptMessage;
  onLinkClick: Props['onLinkClick'];
  streamdownStaggerMs: number;
  streamdownDurationMs: number;
};

type AgentMessageLinkProps = React.ComponentProps<'a'>;

const AgentMessageLinkContext = React.createContext<Props['onLinkClick']>(() => undefined);

/** Adapts Streamdown links to the product's internal and external navigation rules. */
const AgentMessageLink: React.FC<AgentMessageLinkProps> = ({ children, href, ...props }) => {
  const onLinkClick = React.useContext(AgentMessageLinkContext);
  return (
    <a href={href} onClick={(event) => onLinkClick(href ?? '', event)} rel="noreferrer noopener" {...props}>
      {children}
    </a>
  );
};

const agentMessageComponents = { a: AgentMessageLink };

/** Keeps settled bubbles outside the active response's per-delta render path. */
const AgentMessage = React.memo(
  ({ message, onLinkClick, streamdownStaggerMs, streamdownDurationMs }: AgentMessageProps) => (
    <Message from={message.role === ChatRole.USER ? 'user' : 'assistant'}>
      <MessageContent>
        {message.role === ChatRole.AGENT ? (
          <AgentMessageLinkContext.Provider value={onLinkClick}>
            <MessageResponse
              animated={{ duration: streamdownDurationMs, stagger: streamdownStaggerMs }}
              components={agentMessageComponents}
              isAnimating={message.isStreaming}
            >
              {message.content}
            </MessageResponse>
          </AgentMessageLinkContext.Provider>
        ) : (
          message.content
        )}
      </MessageContent>
    </Message>
  ),
  (previous, next) =>
    previous.message.content === next.message.content &&
    previous.message.isStreaming === next.message.isStreaming &&
    previous.message.role === next.message.role &&
    previous.onLinkClick === next.onLinkClick &&
    previous.streamdownStaggerMs === next.streamdownStaggerMs &&
    previous.streamdownDurationMs === next.streamdownDurationMs
);
AgentMessage.displayName = 'AgentMessage';

/**
 * One contiguous run of user and assistant bubbles.
 *
 * The synthetic trailing run (key `AGENT_PENDING_SEGMENT_KEY`) has no messages and renders the
 * pending loader instead, so the transcript owns exactly one loading affordance.
 */
const MessageRunPart: React.FC<Props> = ({ segment, onLinkClick }) => {
  const { t } = useTranslation();
  const { streamdownStaggerMs, streamdownDurationMs } = useAgentStreamingConfig();
  return (
    <>
      {segment.messages.map((message) => (
        <AgentMessage
          key={message.key}
          message={message}
          onLinkClick={onLinkClick}
          streamdownStaggerMs={streamdownStaggerMs}
          streamdownDurationMs={streamdownDurationMs}
        />
      ))}
      {segment.key === AGENT_PENDING_SEGMENT_KEY && (
        <Loader label={t('hiring-dashboard-ai-assistant:agent.dock.streaming')} />
      )}
    </>
  );
};

export default MessageRunPart;
